import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  input,
  model,
  output,
  signal,
  effect,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  SolicitudVacaciones,
  EstatusVacaciones,
  EditarSolicitudAdminRequest,
} from '@schemas/vacaciones.schema';
import { VacacionesService } from '@services/vacaciones.service';

/**
 * Sentinela que las dos dropdowns de estatus usan cuando el editor no
 * quiere cambiar el estatus actual. Se mapea a "no enviar" en el request.
 *
 * Pendiente/Cancelada viven en la dropdown del Director por agrupación
 * visual, pero su stamp se persiste en RH (decisión de negocio: "regresar
 * a Pendiente" y "Cancelar" son acciones administrativas de RH, no
 * resoluciones del Director).
 */
type EstatusEmpleado = 'sin_cambio' | 'Pendiente' | 'Cancelada';
type EstatusDirector = 'sin_cambio' | 'Aprobada Director' | 'Rechazada Director';
type EstatusRH = 'sin_cambio' | 'Aprobada' | 'Rechazada';

import { PageHeader, SectionHeader } from '@intelishs-group/ui';

@Component({
  selector: 'app-solicitud-editar-admin-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NzButtonModule,
    NzModalModule,
    NzSelectModule,
    NzInputModule,
    PageHeader,
    SectionHeader,
  ],
  templateUrl: './solicitud-editar-admin-modal.html',
  styleUrl: './solicitud-editar-admin-modal.css',
})
export class SolicitudEditarAdminModal {
  private readonly vacacionesService = inject(VacacionesService);
  private readonly message = inject(NzMessageService);

  visible = model(false);
  solicitud = input<SolicitudVacaciones | null>(null);

  /**
   * El contexto va en el SUBTÍTULO, nunca en el título, y se arma aquí porque
   * `page-header` recibe un string: con partes opcionales, el encadenado se
   * lee peor en el template.
   */
  subtitulo = computed(() => {
    const sol = this.solicitud();
    if (!sol) return '';
    return [sol.empleadoIdInterno, sol.area].filter(Boolean).join(' · ');
  });
  saved = output<void>();

  editComentarios = signal('');
  editComentariosDirector = signal('');
  editComentariosRH = signal('');
  editGuardando = signal(false);

  /**
   * Estatus seleccionado por sección. Solo UNA de las TRES dropdowns
   * (Empleado / Director / RH) puede tener un valor distinto a
   * "sin_cambio" a la vez (mutua exclusión enforced en los handlers
   * <see cref="onEmpleadoEstatusChange"/> /
   * <see cref="onDirectorEstatusChange"/> / <see cref="onRhEstatusChange"/>).
   * Si las tres quedan en "sin_cambio", el estatus persiste igual.
   */
  editEmpleadoEstatus = signal<EstatusEmpleado>('sin_cambio');
  editDirectorEstatus = signal<EstatusDirector>('sin_cambio');
  editRhEstatus = signal<EstatusRH>('sin_cambio');

  readonly empleadoEstatusOpciones: { value: EstatusEmpleado; label: string }[] = [
    { value: 'sin_cambio', label: 'Sin cambio' },
    { value: 'Pendiente', label: 'Pendiente' },
    { value: 'Cancelada', label: 'Cancelada' },
  ];

  readonly directorEstatusOpciones: { value: EstatusDirector; label: string }[] = [
    { value: 'sin_cambio', label: 'Sin cambio' },
    { value: 'Aprobada Director', label: 'Aprobada Director' },
    { value: 'Rechazada Director', label: 'Rechazada Director' },
  ];

  readonly rhEstatusOpciones: { value: EstatusRH; label: string }[] = [
    { value: 'sin_cambio', label: 'Sin cambio' },
    { value: 'Aprobada', label: 'Aprobada' },
    { value: 'Rechazada', label: 'Rechazada' },
  ];

  /**
   * Snapshot de los valores iniciales al abrir el modal. Se usa en
   * <see cref="guardar"/> para detectar qué sección fue editada y, si lo
   * fue, autostampar al editor como aprobador y la fecha = ahora.
   * Solo Director y RH llevan stamp; el comentario del empleado y el
   * estatus se persisten sin marcar resolución.
   */
  private snapshot = {
    estatus: 'Pendiente' as EstatusVacaciones,
    comentariosDirector: '',
    comentariosRH: '',
  };

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      const sol = this.solicitud();
      if (!sol) return;
      this.editComentarios.set(sol.comentarios ?? '');
      this.editComentariosDirector.set(sol.comentariosDirector ?? '');
      this.editComentariosRH.set(sol.comentariosRH ?? '');
      // Cada apertura arranca con los tres selects en "sin_cambio".
      this.editEmpleadoEstatus.set('sin_cambio');
      this.editDirectorEstatus.set('sin_cambio');
      this.editRhEstatus.set('sin_cambio');
      // Snapshot para detectar ediciones por sección al guardar.
      this.snapshot = {
        estatus: sol.estatus,
        comentariosDirector: sol.comentariosDirector ?? '',
        comentariosRH: sol.comentariosRH ?? '',
      };
    });
  }

  cerrar(): void {
    this.visible.set(false);
  }

  /**
   * Mutua exclusión 3-way: cuando el editor selecciona un estatus en
   * cualquiera de las tres dropdowns, las otras dos vuelven a
   * "sin_cambio". Solo una sección dicta el nuevo estatus en un guardado.
   */
  onEmpleadoEstatusChange(value: EstatusEmpleado): void {
    this.editEmpleadoEstatus.set(value);
    if (value !== 'sin_cambio') {
      this.editDirectorEstatus.set('sin_cambio');
      this.editRhEstatus.set('sin_cambio');
    }
  }

  onDirectorEstatusChange(value: EstatusDirector): void {
    this.editDirectorEstatus.set(value);
    if (value !== 'sin_cambio') {
      this.editEmpleadoEstatus.set('sin_cambio');
      this.editRhEstatus.set('sin_cambio');
    }
  }

  onRhEstatusChange(value: EstatusRH): void {
    this.editRhEstatus.set(value);
    if (value !== 'sin_cambio') {
      this.editEmpleadoEstatus.set('sin_cambio');
      this.editDirectorEstatus.set('sin_cambio');
    }
  }

  guardar(): void {
    const sol = this.solicitud();
    if (!sol) return;

    // ── Estatus final ────────────────────────────────────────────────
    // Solo una de las tres dropdowns tiene valor (mutua exclusión).
    //
    // Mapeo de stamp:
    //   • Empleado dropdown (Pendiente/Cancelada) → stamp RH
    //     (son acciones administrativas: regresar a pendiente o cancelar
    //     una solicitud — las hace RH a nombre de la operación).
    //   • Director dropdown (Aprobada/Rechazada Director) → stamp Director.
    //   • RH dropdown (Aprobada/Rechazada) → stamp RH.
    let estatusFinal: EstatusVacaciones = this.snapshot.estatus;
    let estatusStampaDirector = false;
    let estatusStampaRh = false;

    const empSel = this.editEmpleadoEstatus();
    const dirSel = this.editDirectorEstatus();
    const rhSel = this.editRhEstatus();

    if (empSel !== 'sin_cambio') {
      estatusFinal = empSel as EstatusVacaciones;
      estatusStampaRh = true;
    } else if (dirSel !== 'sin_cambio') {
      estatusFinal = dirSel as EstatusVacaciones;
      estatusStampaDirector = true;
    } else if (rhSel !== 'sin_cambio') {
      estatusFinal = rhSel as EstatusVacaciones;
      estatusStampaRh = true;
    }

    // ── Detección de edición por sección ────────────────────────────
    // Cada sección se considera editada si su estatus stampea esa sección
    // o si cambió alguno de sus campos editables. El comentario del
    // empleado (top) por sí solo NO dispara stamp — es corrección
    // administrativa, no resolución.
    const directorEditado =
      estatusStampaDirector || this.editComentariosDirector() !== this.snapshot.comentariosDirector;

    const rhEditado = estatusStampaRh || this.editComentariosRH() !== this.snapshot.comentariosRH;

    const ahora = new Date().toISOString();

    const request: EditarSolicitudAdminRequest = {
      estatus: estatusFinal,
      comentarios: this.editComentarios() || undefined,
      comentariosDirector: this.editComentariosDirector() || undefined,
      comentariosRH: this.editComentariosRH() || undefined,
      // Autostamp solo si la sección fue editada. El backend resuelve
      // "yo" → kidempleadorh del JWT.
      ...(directorEditado && {
        aprobadorDirectorTipo: 'yo',
        fechaAprobacionDirector: ahora,
      }),
      ...(rhEditado && {
        aprobadorRHTipo: 'yo',
        fechaAprobacionRH: ahora,
      }),
    };

    this.editGuardando.set(true);
    this.vacacionesService.editarAdmin(sol.id, request).subscribe({
      next: () => {
        this.message.success('Solicitud actualizada correctamente');
        this.editGuardando.set(false);
        this.saved.emit();
        this.cerrar();
      },
      error: () => {
        this.message.error('Error al guardar los cambios');
        this.editGuardando.set(false);
      },
    });
  }
}
